// Cancellation-reasons admin manager — design-notes-booking-types-reasons-titles Feature C.
// A single Cancellation reasons list, fully editable (C1): add, edit label, reorder (sort_order),
// retire (soft-delete) + reactivate. Retire shows the in-use count (A1). (The "Booking reasons"
// list was removed — redundant with Booking Kind.) Dedicated lighter manager.

import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { C, F, card, Btn, StatusPill, Modal, ErrorText } from './adminUi';
import * as api from '../../lib/admin';

const rowStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: `1px solid ${C.border}` };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 12, color: C.textDim, marginBottom: 3, display: 'block' };
const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text, width: '100%' };

export default function ReasonCodesManager({ setToast }: { setToast: (s: string | null) => void }) {
  const context: api.ReasonContextValue = 'cancellation'; // only list now (booking reasons removed)
  const [rows, setRows] = useState<api.AdminReasonCode[]>([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [modal, setModal] = useState<{ kind: 'form'; editing: api.AdminReasonCode | null } | { kind: 'retire'; row: api.AdminReasonCode } | null>(null);
  const [reordering, setReordering] = useState(false);

  const load = useCallback(() => { api.listReasonCodes(context, includeInactive).then(setRows); }, [context, includeInactive]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => load();

  // Reorder: rebuild the visible order, renumber to i*10, persist only the rows whose value changed.
  const move = async (idx: number, dir: -1 | 1) => {
    const next = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setReordering(true);
    try {
      for (let i = 0; i < next.length; i++) {
        const desired = i * 10;
        if (next[i].sort_order !== desired) await api.updateReasonCode(next[i].id, { sort_order: desired });
      }
      refresh();
    } finally { setReordering(false); }
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <h1 style={{ fontSize: 17, margin: 0 }}>Cancellation reasons</h1>
        <label style={{ marginLeft: 16, fontSize: 12.5, color: C.textDim, display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} style={{ accentColor: C.accent }} /> include inactive
        </label>
        <div style={{ marginLeft: 'auto' }}><Btn kind="primary" onClick={() => setModal({ kind: 'form', editing: null })}>+ New reason</Btn></div>
      </div>

      <p style={{ color: C.textDim, fontSize: 12.5, margin: '0 0 12px' }}>
        Coded reasons offered when cancelling a booking. Add, rename, reorder, or retire — retired
        reasons stay on past bookings but disappear from the picker.
      </p>

      {rows.length === 0 ? <div style={{ color: C.textMuted, fontStyle: 'italic', padding: 16 }}>None yet — add one.</div> : rows.map((r, i) => (
        <div key={r.id} style={rowStyle}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <button disabled={i === 0 || reordering} onClick={() => move(i, -1)} title="Move up" style={{ border: 'none', background: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? C.borderMed : C.textDim, fontSize: 10, lineHeight: 1, padding: 0 }}>▲</button>
            <button disabled={i === rows.length - 1 || reordering} onClick={() => move(i, 1)} title="Move down" style={{ border: 'none', background: 'none', cursor: i === rows.length - 1 ? 'default' : 'pointer', color: i === rows.length - 1 ? C.borderMed : C.textDim, fontSize: 10, lineHeight: 1, padding: 0 }}>▼</button>
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>{r.label} <StatusPill active={r.is_active} /></div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {r.is_active ? <>
              <Btn kind="ghost" onClick={() => setModal({ kind: 'form', editing: r })}>Edit</Btn>
              <Btn kind="danger" onClick={() => setModal({ kind: 'retire', row: r })}>Retire</Btn>
            </> : <Btn kind="accent" onClick={async () => { const res = await api.reactivateReasonCode(r.id); if (!res.error) { setToast(`${r.label} reactivated.`); refresh(); } else setToast(res.error); }}>Reactivate</Btn>}
          </div>
        </div>
      ))}

      {modal?.kind === 'form' && (
        <ReasonForm editing={modal.editing} context={context} nextSort={rows.length * 10} onClose={() => setModal(null)}
          onSaved={(msg) => { setModal(null); setToast(msg); refresh(); }} />
      )}
      {modal?.kind === 'retire' && (
        <RetireDialog row={modal.row} onClose={() => setModal(null)}
          onDone={() => { setModal(null); setToast(`${modal.row.label} retired.`); refresh(); }} />
      )}
    </div>
  );
}

function ReasonForm({ editing, context, nextSort, onClose, onSaved }: { editing: api.AdminReasonCode | null; context: api.ReasonContextValue; nextSort: number; onClose: () => void; onSaved: (msg: string) => void }) {
  const [label, setLabel] = useState(editing?.label ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = label !== (editing?.label ?? '');

  const submit = async () => {
    setError(null);
    if (!label.trim()) { setError('Label is required.'); return; }
    setBusy(true);
    const res = editing
      ? await api.updateReasonCode(editing.id, { label: label.trim() })
      : await api.createReasonCode({ context, label: label.trim(), sort_order: nextSort });
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onSaved(editing ? 'Saved.' : 'Reason added.');
  };

  return (
    <Modal title={editing ? `Edit reason` : `New ${context} reason`} onClose={onClose} width={420} dirty={dirty}>
      <div style={{ marginBottom: 8 }}>
        <label style={labelStyle}>Label <span style={{ color: C.red }}>*</span></label>
        <input value={label} placeholder={context === 'booking' ? 'e.g. Check ride' : 'e.g. Weather'} onChange={(e) => setLabel(e.target.value)} style={inputStyle} autoFocus />
      </div>
      <ErrorText>{error}</ErrorText>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="primary" disabled={busy} onClick={submit}>{editing ? 'Save' : 'Add'}</Btn>
      </div>
    </Modal>
  );
}

function RetireDialog({ row, onClose, onDone }: { row: api.AdminReasonCode; onClose: () => void; onDone: () => void }) {
  const [count, setCount] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api.reasonCodeImpact(row.id).then(setCount); }, [row.id]);

  const confirm = async () => {
    setBusy(true); setError(null);
    const res = await api.retireReasonCode(row.id);
    setBusy(false);
    if (res.error) { setError(res.error); return; }
    onDone();
  };

  return (
    <Modal title={`Retire "${row.label}"`} onClose={onClose} width={420}>
      <p style={{ fontSize: 13, color: C.text, margin: '0 0 8px' }}>
        {count === null ? 'Checking how many bookings use this reason…'
          : count === 0 ? 'No bookings use this reason yet.'
          : `${count} booking${count === 1 ? '' : 's'} use this reason — they keep it. It just won't appear in the picker for new ones.`}
      </p>
      <p style={{ fontSize: 12.5, color: C.textDim, margin: 0 }}>Reversible — reactivate any time. Nothing is deleted.</p>
      <ErrorText>{error}</ErrorText>
      <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
        <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
        <Btn kind="danger" disabled={busy || count === null} onClick={confirm}>Retire reason</Btn>
      </div>
    </Modal>
  );
}
